import React, {
  useEffect, useState, forwardRef, useRef,
} from "react";

import { AutoComplete, Input } from "antd";

import StyledTooltip from "../../../Common/UIComponents/StyledTooltip";
import MaxCharacterCount from "./MaxCharacterCount";

import { ReactComponent as ExclamationIcon } from "../../../../assets/images/sample-submission/exclamationIcon.svg";

import "./ErrorCheckingInput.css";

const ErrorCheckingInput = forwardRef(({
  label,
  labelDivClassName,
  inputID,
  value,
  invalidChars, // special chars are escaped
  invalidKeys, // special chars are not escaped
  onChange,
  onKeyDown,
  onErrorChange,
  onClickAway,
  onFocus,
  charLimit,
  letInputExceedCharLimit,
  customClassName = "",
  placeholderText,
  hasError = false,
  showLabel = false,
  remainingLimit = 0,
  formatValidator,
  formatPlaceholder,
  labelRequired = false,
}, ref) => {
  const [inputErrorSet, setInputErrorSet] = useState(new Set());
  const [formatError, setFormatError] = useState(null);
  const [internalValue, setInternalValue] = useState("");
  const [touched, setTouched] = useState({ touched: false, dirty: false });
  const inputRef = useRef();
  const isMounted = useRef(false);

  /**
   * During the time of edit, if value is already there,
   * setting touched and dirty to true for showing input error state
   */
  useEffect(() => {
    if (!isMounted.current && value) {
      if (value.length > 0) {
        setTouched({ touched: true, dirty: true });
      }
      isMounted.current = true;
    }
  }, [value]);

  /** Update value when it changes from the parent */
  useEffect(() => {
    if (value !== undefined && value !== internalValue) {
      setInternalValue(value);
      inputRef.current.value = value;
    }
    if (value === undefined) {
      setInternalValue("");
    }
  }, [value]); // eslint-disable-line

  /** Check if value contains any invalid characters */
  useEffect(() => {
    if (ref !== undefined && ref !== null && invalidChars?.length > 0) {
      const regex = new RegExp(invalidChars.join("|"), "g");
      const invalidMatches = internalValue?.match(regex);
      if (invalidMatches) {
        setInputErrorSet(new Set(invalidMatches));
      } else {
        setInputErrorSet(new Set());
      }
    }

    // Format validation (date/time)
    if (formatValidator && internalValue) {
      const { isValid, errorMessage } = formatValidator(internalValue);
      setFormatError(isValid ? null : errorMessage);
    } else {
      setFormatError(null);
    }
  }, [internalValue]); // eslint-disable-line

  /** Call onErrorChange if errors change */
  useEffect(() => {
    if (onErrorChange) {
      // Include format error in error reporting
      const hasFormatError = formatError !== null;
      const combinedErrorSet = new Set(inputErrorSet);
      if (hasFormatError) {
        combinedErrorSet.add("format_error");
      }
      onErrorChange(combinedErrorSet);
    }
  }, [inputErrorSet.size, formatError]); // eslint-disable-line

  /** Update internal value and call onChange */
  const onChangeInternal = (val) => {
    setInternalValue(val);
    if (val.length > 0) {
      setTouched({ touched: true, dirty: true });
    }
    if (onChange) {
      onChange(val);
    }
  };

  /** Handle focus */
  const onFocusInternal = () => {
    setTouched((state) => ({ ...state, touched: true }));
    if (onFocus) {
      onFocus();
    }
  };

  /** Handle blur and trim input */
  const onBlurInternal = () => {
    if (onClickAway) {
      setInternalValue(internalValue.trim());
      onClickAway(internalValue.trim());
    }
  };

  /** Prevent entry of invalid keys */
  const onKeyDownInternal = (e) => {
    if (invalidKeys && invalidKeys.includes(e.key)) {
      e.preventDefault();
    }
    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  const currentValue = internalValue ?? "";
  const showExternalError = touched.touched && touched.dirty && currentValue.length === 0 && hasError;
  const hasInternalError = inputErrorSet.size !== 0 || formatError !== null;
  const hasStatusError = hasInternalError || showExternalError;

  return (
    <>
      <div className={labelDivClassName}>
        {showLabel && (
          <label htmlFor={inputID}>
            {label}
            {labelRequired && (
              <span className="SampleDetails__mandatory-asterisk"> *</span>
            )}
          </label>
        )}
        {hasInternalError && (
          <StyledTooltip
            variant="light"
            title={(
              <div className="SampleSubmission__InvalidCharsTooltipTitle">
                {formatError && (
                  <span className="SampleSubmission__FormatError">{formatError}</span>
                )}
                {inputErrorSet.size !== 0 && (
                  <>
                    <span>Invalid input:</span>
                    {Array.from(inputErrorSet).map((char, i) => (
                      <li key={i} className="SampleSubmission__InvalidChar">
                        {char === " " ? (
                          <span className="SampleSubmission__InvalidChar__SpaceHighlight">{"\u23B5"}</span>
                        ) : (
                          <span>{char}</span>
                        )}
                      </li>
                    ))}
                  </>
                )}
              </div>
            )}
            placement="right"
          >
            <ExclamationIcon width={13} height={13} />
          </StyledTooltip>
        )}
      </div>
      <div className="SampleDetailsInput">
        <AutoComplete
          allowClear
          value={currentValue}
          placeholder={formatPlaceholder || placeholderText}
          onFocus={onFocusInternal}
          onBlur={onBlurInternal}
          maxLength={charLimit && !letInputExceedCharLimit ? charLimit : undefined}
          onKeyDown={onKeyDownInternal}
          onClear={() => onChangeInternal("")}
        >
          <Input
            ref={(component) => {
              ref(component?.input);
              inputRef.current = component;
            }}
            className={customClassName}
            status={hasStatusError ? "error" : undefined}
            onChange={(e) => onChangeInternal(e.target.value)}
          />
        </AutoComplete>
        {charLimit && (
          <MaxCharacterCount
            customMaxCharClassName="SampleDetailsInput__MaxChar"
            charCount={remainingLimit}
            charLimit={charLimit}
          />
        )}
      </div>
    </>
  );
});

export default ErrorCheckingInput;
